import { EnemyType } from '../types/Combat.types';
import { InventoryManager } from './InventoryManager';
import { ActMap } from '../types/Map.types';
import { generateActMap } from './MapGenerator';
import { MetaManager } from './MetaManager';
import { CampManager, Survivor, SurvivorClass } from './CampManager';
import { PERSONAL_SLOTS } from '../types/Item.types';

/** Membre de l'équipe pour un run */
export interface TeamMember {
  id: string;           // 'captain' ou survivor.id
  name: string;
  class: SurvivorClass;
  survivorRef: Survivor | null; // null = capitaine
  hp: number;           // HP persistant pendant le run
  maxHp: number;
  alive: boolean;
}

// === GESTIONNAIRE DE RUN ===
// Gère la carte, la progression et l'état du joueur pendant un run.
// L'inventaire complet passe par InventoryManager (source de vérité unique).

/** Configuration d'un combat (interface legacy conservée pour compatibilité CombatScene) */
export interface CombatConfig {
  combatNumber: number;
  enemies: { type: EnemyType; col: number; row: number }[];
  isBoss: boolean;
}

export class RunManager {
  /** Inventaire unique de l'équipe (équipements, transport, consommables) */
  private inventory: InventoryManager;
  private playerHp: number;
  private gold: number = 0;
  private map: ActMap;
  private combatsCompleted: number = 0;
  private _meta: MetaManager;
  private _camp: CampManager;
  private _selectedSurvivor: Survivor | null = null;
  private _selectedClass: SurvivorClass = 'warrior';
  private _team: TeamMember[] = [];
  /** Recrutements en attente d'application au camp (fin de run victoire uniquement). Sprint Bug fix. */
  private _pendingRecruits: Array<{ class: SurvivorClass; name: string }> = [];
  /** Timestamp de démarrage (ms) — utilisé pour la durée envoyée au leaderboard */
  private _startedAt: number = Date.now();

  constructor(meta?: MetaManager, camp?: CampManager) {
    this._meta = meta ?? new MetaManager();
    this._camp = camp ?? new CampManager();
    this.inventory = new InventoryManager();
    this.playerHp = -1;
    this.gold = 15;
    this.map = generateActMap();
  }

  /** Durée écoulée depuis le début du run, en secondes. */
  getDurationSec(): number {
    return Math.max(0, Math.floor((Date.now() - this._startedAt) / 1000));
  }

  /** Étage atteint (le plus haut visité). Sert au leaderboard. */
  getMaxFloorReached(): number {
    const visited = this.map.nodes.filter(n => n.status === 'completed' || n.status === 'current');
    if (visited.length === 0) return 1;
    return Math.max(...visited.map(n => n.floor)) + 1;
  }

  /**
   * Sprint 82 v3 — Factory pour le combat secret Captain Jack Warraps.
   * Crée un RunManager avec une map à 1 seul nœud boss contenant Jack.
   * Le joueur passe par RunPrepScene → MapScene → CombatScene normalement.
   */
  static createJackDuelRun(meta: MetaManager, camp: CampManager): RunManager {
    const run = new RunManager(meta, camp);
    // Remplacer la map normale par une map d'1 seul nœud boss
    const startNode = {
      id: '0-0',
      floor: 0,
      index: 0,
      type: 'combat' as const,
      status: 'completed' as const,
      x: 0.5,
      connections: ['1-0'],
    };
    const jackNode = {
      id: '1-0',
      floor: 1,
      index: 0,
      type: 'boss' as const,
      status: 'available' as const,
      x: 0.5,
      connections: [],
      combatConfig: {
        enemies: [{ type: 'boss_jack' as EnemyType, col: 6, row: 4 }],
        isBoss: true,
      },
    };
    run.map = {
      nodes: [startNode, jackNode],
      currentNodeId: startNode.id,
      totalFloors: 2,
    };
    return run;
  }

  /** Vérifie si ce run est un duel secret contre Captain Jack. */
  isJackDuel(): boolean {
    // Heuristique : 1 seul vrai nœud combat + boss_jack à l'intérieur
    if (this.map.totalFloors !== 2) return false;
    const bossNode = this.map.nodes.find(n => n.type === 'boss');
    return !!bossNode?.combatConfig?.enemies.some(e => e.type === 'boss_jack');
  }

  getMeta(): MetaManager {
    return this._meta;
  }

  getCamp(): CampManager {
    return this._camp;
  }

  /** Définir le survivant sélectionné pour ce run */
  setSelectedSurvivor(survivor: Survivor | null): void {
    this._selectedSurvivor = survivor;
    this._selectedClass = survivor?.class ?? 'warrior';
  }

  getSelectedSurvivor(): Survivor | null {
    return this._selectedSurvivor;
  }

  getSelectedClass(): SurvivorClass {
    return this._selectedClass;
  }

  // === ÉQUIPE ===

  /** Définir l'équipe pour ce run (appelé depuis RunPrepScene) */
  setTeam(members: TeamMember[]): void {
    this._team = members;
    // Rétro-compat : le premier membre est le "selected"
    if (members.length > 0) {
      this._selectedSurvivor = members[0].survivorRef;
      this._selectedClass = members[0].class;
    }
  }

  getTeam(): TeamMember[] {
    return this._team;
  }

  // === RECRUTEMENTS DIFFÉRÉS ===
  // Un survivant rencontré sur la map est promis : il rejoint le camp UNIQUEMENT
  // si le run se termine par une victoire (extraction ou boss final). Sinon perdu.
  // Évite de gonfler le dortoir + tout level up parasite avec un perso qui n'a rien fait.

  /** Met en attente un survivant rencontré pendant le run. */
  queueRecruit(survivorClass: SurvivorClass, name: string): void {
    this._pendingRecruits.push({ class: survivorClass, name });
  }

  /** Liste des recrutements en attente (lecture seule). */
  getPendingRecruits(): ReadonlyArray<{ class: SurvivorClass; name: string }> {
    return this._pendingRecruits;
  }

  /**
   * Applique les recrutements en attente au camp (appelé à la fin d'un run victorieux).
   * Vide la file après application.
   */
  applyPendingRecruits(): void {
    for (const r of this._pendingRecruits) {
      this._camp.addSurvivor(r.class, r.name);
    }
    this._pendingRecruits = [];
  }

  /** Met à jour les HP d'un membre après un combat */
  setTeamMemberHp(memberId: string, hp: number): void {
    const m = this._team.find(t => t.id === memberId);
    if (m) m.hp = hp;
  }

  /** Marquer un membre comme mort (permadeath) */
  killTeamMember(memberId: string): void {
    const m = this._team.find(t => t.id === memberId);
    if (m) {
      m.alive = false;
      m.hp = 0;
      // Si c'est un survivant recruté, le tuer aussi au camp
      if (m.survivorRef) {
        this._camp.killSurvivor(m.survivorRef.id);
      }
    }
  }

  /** Retourne les membres vivants de l'équipe */
  getAliveTeam(): TeamMember[] {
    return this._team.filter(t => t.alive);
  }

  // === INVENTAIRE (source de vérité unique) ===

  getInventory(): InventoryManager {
    return this.inventory;
  }

  // === CARTE ===

  getMap(): ActMap {
    return this.map;
  }

  /** Sélectionne un nœud sur la carte (le joueur s'y déplace) */
  selectMapNode(nodeId: string): void {
    const node = this.map.nodes.find(n => n.id === nodeId);
    if (!node || node.status !== 'available') return;

    // Marquer l'ancien nœud courant comme complété
    if (this.map.currentNodeId) {
      const prev = this.map.nodes.find(n => n.id === this.map.currentNodeId);
      if (prev) prev.status = 'completed';
    }

    // Marquer le nouveau nœud comme courant
    node.status = 'current';
    this.map.currentNodeId = nodeId;

    // Verrouiller tous les nœuds non complétés du même étage (on a choisi notre chemin)
    for (const n of this.map.nodes) {
      if (n.floor === node.floor && n.id !== nodeId && n.status === 'available') {
        n.status = 'locked';
      }
    }

    // Auto-save à chaque changement de nœud
    this.saveRunState();
  }

  /** Marque le nœud courant comme complété et débloque les suivants */
  completeCurrentNode(): void {
    const current = this.map.nodes.find(n => n.id === this.map.currentNodeId);
    if (!current) return;

    current.status = 'completed';
    this.combatsCompleted++;

    // Débloquer les nœuds connectés
    for (const connId of current.connections) {
      const next = this.map.nodes.find(n => n.id === connId);
      if (next && next.status === 'locked') {
        next.status = 'available';
      }
    }

    // Auto-save après complétion
    this.saveRunState();
  }

  /** Retourne la config de combat du nœud courant */
  getCurrentCombatConfig(): CombatConfig {
    const current = this.map.nodes.find(n => n.id === this.map.currentNodeId);
    const config = current?.combatConfig;

    if (!config) {
      // Fallback : combat basique
      return {
        combatNumber: this.combatsCompleted + 1,
        enemies: [{ type: 'zombie', col: 5, row: 3 }],
        isBoss: false,
      };
    }

    return {
      combatNumber: this.combatsCompleted + 1,
      enemies: [...config.enemies],
      isBoss: config.isBoss,
    };
  }

  /** Retourne le type du nœud courant */
  getCurrentNodeType(): string {
    const current = this.map.nodes.find(n => n.id === this.map.currentNodeId);
    return current?.type ?? 'combat';
  }

  /** Numéro de combat actuel (pour affichage) */
  getCurrentCombatNumber(): number {
    return this.combatsCompleted + 1;
  }

  /** Nombre total de combats sur la carte */
  getTotalCombats(): number {
    return this.map.nodes.filter(n => n.type === 'combat' || n.type === 'elite' || n.type === 'boss').length;
  }

  /** Label "Étage X/Y" pour l'affichage en combat */
  getCurrentFloorLabel(): string {
    const current = this.map.nodes.find(n => n.id === this.map.currentNodeId);
    const floor = current ? current.floor + 1 : 1;
    return `Étage ${floor}/${this.map.totalFloors}`;
  }

  /** Vérifie si le nœud courant est le boss final (pas le boss intermédiaire) */
  isLastCombat(): boolean {
    const current = this.map.nodes.find(n => n.id === this.map.currentNodeId);
    if (current?.type !== 'boss') return false;
    // Le boss final est celui au floor le plus élevé parmi tous les nœuds boss
    const maxBossFloor = Math.max(...this.map.nodes.filter(n => n.type === 'boss').map(n => n.floor));
    return current.floor === maxBossFloor;
  }

  /** Vérifie si le run est terminé (boss vaincu) */
  isRunComplete(): boolean {
    const bossNode = this.map.nodes.find(n => n.type === 'boss');
    return bossNode?.status === 'completed';
  }

  // === HP JOUEUR ===
  // Note v0.38.12 : pour ne pas casser les scènes legacy (ExploreScene,
  // LootScene, DevScene) qui utilisent getPlayerHp/setPlayerHp en mode
  // « single-player », on lit/écrit directement sur le 1er membre vivant
  // de l'équipe quand elle existe. Sinon fallback sur le champ playerHp
  // (cas où aucune équipe n'est encore définie, ex. tests unitaires).
  // Sans ça, soigner via LootScene ou subir un piège dans ExploreScene
  // n'avait AUCUN effet sur le combat suivant : team[0].hp n'était pas
  // touché, et CombatScene.create() lit team[i].hp au boot.

  getPlayerHp(): number {
    const firstAlive = this._team.find(m => m.alive);
    if (firstAlive) return firstAlive.hp;
    return this.playerHp;
  }

  setPlayerHp(hp: number): void {
    this.playerHp = hp;
    const firstAlive = this._team.find(m => m.alive);
    if (firstAlive) firstAlive.hp = Math.max(0, Math.min(hp, firstAlive.maxHp));
  }

  /** Retourne le max HP du premier joueur vivant (stats classe + bonus équipement) */
  getPlayerMaxHp(): number {
    const alive = this.getAliveTeam();
    if (alive.length > 0) return alive[0].maxHp;
    return 20; // fallback
  }

  /** Soigne tous les membres vivants de l'équipe d'un pourcentage de leurs HP max.
   * Ignore les membres morts. Retourne le soin effectivement appliqué par membre. */
  healAliveTeam(percent: number): Array<{ id: string; name: string; healed: number }> {
    const results: Array<{ id: string; name: string; healed: number }> = [];
    for (const member of this._team) {
      if (!member.alive) continue;
      const heal = Math.floor(member.maxHp * percent);
      const before = member.hp;
      member.hp = Math.min(member.hp + heal, member.maxHp);
      results.push({ id: member.id, name: member.name, healed: member.hp - before });
    }
    // Sync rétro-compat : playerHp = HP du premier membre vivant
    const firstAlive = this._team.find(m => m.alive);
    if (firstAlive) this.playerHp = firstAlive.hp;
    return results;
  }

  /** Applique un delta HP plat (+ pour soin, − pour dégâts) à chaque membre vivant.
   * Soin clampé à maxHp, dégâts clampés à 1 minimum (un événement ne tue jamais).
   * Utilisé par les événements narratifs (EventScene) pour soigner/blesser toute l'équipe.
   * Retourne le delta effectivement appliqué par membre. */
  applyHpDeltaToAliveTeam(delta: number): Array<{ id: string; name: string; applied: number }> {
    const results: Array<{ id: string; name: string; applied: number }> = [];
    for (const member of this._team) {
      if (!member.alive) continue;
      const before = member.hp;
      // Clamp : entre 1 (un événement ne tue pas) et maxHp
      member.hp = Math.min(Math.max(1, member.hp + delta), member.maxHp);
      results.push({ id: member.id, name: member.name, applied: member.hp - before });
    }
    // Sync rétro-compat : playerHp = HP du premier membre vivant
    const firstAlive = this._team.find(m => m.alive);
    if (firstAlive) this.playerHp = firstAlive.hp;
    return results;
  }

  // === OR ===

  getGold(): number {
    return this.gold;
  }

  addGold(amount: number): void {
    this.gold = Math.max(0, this.gold + amount);
    // Tracking or gagné/dépensé
    if (amount > 0) this._meta.addStat('totalGoldEarned', amount);
    else if (amount < 0) this._meta.addStat('totalGoldSpent', -amount);
  }

  // === SAUVEGARDE MID-RUN ===

  private static readonly SAVE_KEY = 'deadtide_run_save';

  /** Sérialise l'état du run en cours */
  saveRunState(): void {
    try {
      const state = {
        playerHp: this.playerHp,
        gold: this.gold,
        combatsCompleted: this.combatsCompleted,
        selectedClass: this._selectedClass,
        startedAt: this._startedAt,
        // Carte : statut de chaque nœud + nœud courant
        map: {
          currentNodeId: this.map.currentNodeId,
          totalFloors: this.map.totalFloors,
          nodes: this.map.nodes.map(n => ({
            id: n.id, floor: n.floor, index: n.index, type: n.type,
            status: n.status, x: n.x, connections: n.connections,
            combatConfig: n.combatConfig,
          })),
        },
        // Équipe : sans survivorRef (reconstitué au load)
        team: this._team.map(m => ({
          id: m.id, name: m.name, class: m.class,
          survivorRefId: m.survivorRef?.id ?? null,
          hp: m.hp, maxHp: m.maxHp, alive: m.alive,
        })),
        // Inventaire complet (nouvelle sérialisation)
        inventory: this.inventory.serialize(),
      };
      localStorage.setItem(RunManager.SAVE_KEY, JSON.stringify(state));
    } catch {
      // localStorage plein ou indisponible
    }
  }

  /** Vérifie s'il y a un run sauvegardé */
  static hasSavedRun(): boolean {
    try {
      return localStorage.getItem(RunManager.SAVE_KEY) !== null;
    } catch {
      return false;
    }
  }

  /** Restaure un run sauvegardé. Retourne null si aucune sauvegarde. */
  static loadSavedRun(meta: MetaManager, camp: CampManager): RunManager | null {
    try {
      const raw = localStorage.getItem(RunManager.SAVE_KEY);
      if (!raw) return null;
      const state = JSON.parse(raw);

      const run = new RunManager(meta, camp);

      // Restaurer les valeurs simples
      run.playerHp = state.playerHp ?? -1;
      run.gold = state.gold ?? 15;
      run.combatsCompleted = state.combatsCompleted ?? 0;
      run._selectedClass = state.selectedClass ?? 'warrior';
      run._startedAt = typeof state.startedAt === 'number' ? state.startedAt : Date.now();

      // Restaurer la carte
      if (state.map) {
        run.map = {
          currentNodeId: state.map.currentNodeId,
          totalFloors: state.map.totalFloors,
          nodes: state.map.nodes,
        };
        // Sanitize : si on quitte mid-action (combat/event/shop pas terminé),
        // le nœud est sauvé en 'current'. Au reload, on le rebascule en
        // 'available' pour qu'il redevienne cliquable normalement (et que
        // selectMapNode() fonctionne dessus). currentNodeId est aussi reset.
        // Sans ça : softlock sur la map (le combat à reprendre n'était pas cliquable).
        for (const n of run.map.nodes) {
          if (n.status === 'current') {
            n.status = 'available';
          }
        }
        if (run.map.currentNodeId) {
          const cn = run.map.nodes.find(n => n.id === run.map.currentNodeId);
          // Si le currentNodeId pointait sur un nœud qui n'est ni completed
          // ni current (ex : on l'a basculé en available ci-dessus), on le clear
          if (cn && cn.status === 'available') {
            run.map.currentNodeId = null;
          }
        }
      }

      // Restaurer l'équipe (reconstituer survivorRef depuis le camp)
      if (Array.isArray(state.team)) {
        const survivors = camp.getSurvivors();
        run._team = state.team.map((m: any) => ({
          id: m.id,
          name: m.name,
          class: m.class,
          survivorRef: m.survivorRefId ? survivors.find((s: Survivor) => s.id === m.survivorRefId) ?? null : null,
          hp: m.hp,
          maxHp: m.maxHp,
          alive: m.alive,
        }));
        if (run._team.length > 0) {
          run._selectedSurvivor = run._team[0].survivorRef;
          run._selectedClass = run._team[0].class;
        }
      }

      // Restaurer l'inventaire (nouveau format ou migration legacy)
      if (state.inventory && state.inventory.equipments !== undefined) {
        // Nouveau format : désérialisation directe
        run.inventory = InventoryManager.deserialize(state.inventory);
      } else {
        // Migration depuis l'ancien format (legacy)
        run.inventory = RunManager.migrateLegacyInventory(state);
      }

      return run;
    } catch {
      RunManager.clearSavedRun();
      return null;
    }
  }

  /** Migration des anciennes sauvegardes vers le nouveau format */
  private static migrateLegacyInventory(state: any): InventoryManager {
    const mgr = new InventoryManager();

    // Ancien format : equipment au top-level + inventory (items dans le sac)
    if (state.equipment) {
      const equip = mgr.getEquipment('captain');
      for (const slot of PERSONAL_SLOTS) {
        if (state.equipment[slot]) {
          equip[slot] = { uid: state.equipment[slot].uid, definitionId: state.equipment[slot].definitionId };
        }
      }
      // Ancien backpack
      if (state.equipment.backpack) {
        mgr.equipBackpack({ uid: state.equipment.backpack.uid, definitionId: state.equipment.backpack.definitionId });
      }
    }

    // Ancien sac = transport
    if (Array.isArray(state.inventory)) {
      for (const item of state.inventory) {
        mgr.addToTransport({ uid: item.uid, definitionId: item.definitionId });
      }
    }

    // Anciens consommables
    if (Array.isArray(state.consumables)) {
      for (const c of state.consumables) {
        mgr.addConsumable(c.definitionId);
      }
    }

    // Recalculer nextUid depuis les UIDs réels restaurés pour éviter les collisions
    // (les items legacy ont des UIDs existants, le compteur doit repartir après le max)
    mgr.fixNextUid();

    return mgr;
  }

  /** Supprime la sauvegarde de run */
  static clearSavedRun(): void {
    try {
      localStorage.removeItem(RunManager.SAVE_KEY);
    } catch { /* */ }
  }
}
